<template>
  <div class="max-w-4xl mx-auto space-y-8 animate-in fade-in duration-500">
    <!-- Header Section -->
    <div class="flex flex-col md:flex-row md:items-end justify-between gap-6 pb-6 border-b border-border">
      <div class="space-y-1">
        <h2 class="text-3xl font-black tracking-tighter text-foreground uppercase italic">Paramètres <span class="text-indigo-500">Financiers</span></h2>
        <p class="text-[10px] font-bold uppercase tracking-widest text-muted-foreground antialiased">Configuration des nomenclatures et taxonomies</p>
      </div>
      <Button v-if="activeTab !== 'account' && activeTab !== 'api-keys'" variant="finance" size="lg" shadow="xl" class="font-black uppercase tracking-widest h-12 text-xs shadow-xl shadow-black/20" @click="openAddModal(null)">
        <Plus class="w-4 h-4 mr-2" />
        Nouvelle Racine
      </Button>
    </div>

    <!-- Main Tabs -->
    <Card class="border-border shadow-2xl shadow-black/20 bg-card overflow-hidden">
      <Tabs v-model="activeTab" class="w-full">
        <TabsList class="w-full justify-start rounded-none border-b border-border bg-muted/30 p-2 h-auto">
          <TabsTrigger value="account" class="flex-1 md:flex-none font-black uppercase tracking-widest text-[10px] py-3 px-6 data-[state=active]:bg-zinc-800 data-[state=active]:text-foreground rounded-xl transition-all">
            <UserIcon class="w-3.5 h-3.5 mr-2" />
            Compte
          </TabsTrigger>
          <TabsTrigger value="investments" class="flex-1 md:flex-none font-black uppercase tracking-widest text-[10px] py-3 px-6 data-[state=active]:bg-zinc-800 data-[state=active]:text-foreground rounded-xl transition-all">
            <LayoutGrid class="w-3.5 h-3.5 mr-2" />
            Catégories d'Actifs
          </TabsTrigger>
          <TabsTrigger value="movements" class="flex-1 md:flex-none font-black uppercase tracking-widest text-[10px] py-3 px-6 data-[state=active]:bg-zinc-800 data-[state=active]:text-foreground rounded-xl transition-all">
            <ArrowRightLeft class="w-3.5 h-3.5 mr-2" />
            Catégories de Flux
          </TabsTrigger>
          <TabsTrigger value="api-keys" class="flex-1 md:flex-none font-black uppercase tracking-widest text-[10px] py-3 px-6 data-[state=active]:bg-zinc-800 data-[state=active]:text-foreground rounded-xl transition-all">
            <KeyRound class="w-3.5 h-3.5 mr-2" />
            Clés API
          </TabsTrigger>
        </TabsList>

        <div class="p-8">
          <div class="bg-muted/10 border border-border rounded-2xl min-h-[400px] relative overflow-hidden">
            <div v-if="loading" class="absolute inset-0 flex flex-col items-center justify-center bg-zinc-950/60 backdrop-blur-sm z-10 animate-in fade-in duration-300">
              <RefreshCw class="w-8 h-8 text-indigo-400 animate-spin mb-4" />
              <span class="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Synchronisation...</span>
            </div>
            <div v-if="activeTab === 'account'" class="p-8 space-y-8 animate-in slide-in-from-left duration-300">
              <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div class="space-y-4">
                  <div class="space-y-2">
                    <label class="text-[10px] font-black uppercase tracking-widest text-muted-foreground ml-1">Adresse Email (Non modifiable)</label>
                    <input :value="accountForm.email" type="email" disabled 
                           class="w-full bg-muted/30 border border-border rounded-2xl p-4 text-muted-foreground font-bold cursor-not-allowed" />
                  </div>
                </div>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div class="space-y-2">
                    <label class="text-[10px] font-black uppercase tracking-widest text-muted-foreground ml-1">Prénom</label>
                    <input v-model="accountForm.firstName" type="text" placeholder="Prénom" 
                           class="w-full bg-muted/30 border border-border rounded-2xl p-4 font-bold focus:ring-4 focus:ring-primary/10 outline-none transition-all text-foreground" />
                  </div>
                  <div class="space-y-2">
                    <label class="text-[10px] font-black uppercase tracking-widest text-muted-foreground ml-1">Nom</label>
                    <input v-model="accountForm.lastName" type="text" placeholder="Nom" 
                           class="w-full bg-muted/30 border border-border rounded-2xl p-4 font-bold focus:ring-4 focus:ring-primary/10 outline-none transition-all text-foreground" />
                  </div>
                </div>
              </div>
              
              <div class="flex justify-end pt-4">
                <Button variant="finance" size="lg" :disabled="savingAccount" class="min-w-[200px] font-black uppercase tracking-widest h-12 text-xs" @click="saveAccount">
                  <RefreshCw v-if="savingAccount" class="w-4 h-4 mr-2 animate-spin" />
                  <Check v-else class="w-4 h-4 mr-2" />
                  Sauvegarder le profil
                </Button>
              </div>
            </div>

            <!-- API Keys tab -->
            <div v-else-if="activeTab === 'api-keys'" class="p-8 animate-in slide-in-from-right duration-300 space-y-8">

              <!-- Doc link -->
              <div class="rounded-2xl border border-border bg-muted/10 px-5 py-4 flex items-center justify-between gap-4">
                <div class="flex items-center gap-3 min-w-0">
                  <div class="shrink-0 w-8 h-8 rounded-lg bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center">
                    <FileJson class="w-4 h-4 text-indigo-400" />
                  </div>
                  <div class="min-w-0">
                    <p class="text-[11px] font-black uppercase tracking-widest text-foreground">Documentation OpenAPI</p>
                    <p class="text-[11px] text-muted-foreground truncate">/api/doc/finance.json</p>
                  </div>
                </div>
                <div class="flex items-center gap-2 shrink-0">
                  <a
                    href="/api/doc/finance.json"
                    target="_blank"
                    rel="noopener"
                    class="inline-flex items-center gap-1.5 rounded-xl border border-border bg-muted/20 hover:bg-muted/40 px-3 py-1.5 text-[10px] font-black uppercase tracking-widest text-muted-foreground hover:text-foreground transition-all"
                  >
                    <ExternalLink class="w-3 h-3" />
                    JSON
                  </a>
                  <a
                    href="/api/doc/finance"
                    target="_blank"
                    rel="noopener"
                    class="inline-flex items-center gap-1.5 rounded-xl border border-indigo-500/30 bg-indigo-500/10 hover:bg-indigo-500/20 px-3 py-1.5 text-[10px] font-black uppercase tracking-widest text-indigo-400 hover:text-indigo-300 transition-all"
                  >
                    <ExternalLink class="w-3 h-3" />
                    Swagger UI
                  </a>
                </div>
              </div>

              <!-- Keys manager -->
              <div class="space-y-1">
                <h3 class="text-[11px] font-black uppercase tracking-widest text-foreground">Clés d'accès</h3>
                <p class="text-xs text-muted-foreground">Générez des clés pour permettre à des agents IA ou scripts externes d'accéder à vos données Finance en lecture seule.</p>
              </div>
              <ApiKeyManager />
            </div>

            <div v-else class="p-1 space-y-1">
              <div v-if="!loading && treeData.length === 0" class="p-20 text-center flex flex-col items-center justify-center space-y-4 animate-in fade-in duration-500">
                <div class="w-16 h-16 bg-muted/30 rounded-full flex items-center justify-center text-muted-foreground border border-border child:w-8 child:h-8">
                  <FolderTree />
                </div>
                <div class="space-y-1">
                  <p class="text-sm font-black text-foreground">Arborescence vide</p>
                  <p class="text-[10px] text-muted-foreground font-medium italic">Commencez par créer une catégorie racine pour organiser vos données.</p>
                </div>
              </div>

              <CategoryNode 
                v-for="node in treeData" 
                :key="node.id" 
                :node="node"
                :show-merge="activeTab === 'movements'"
                @add="openAddModal"
                @edit="openEditModal"
                @delete="deleteCategory"
                @merge="openMergeModal"
              />
            </div>
          </div>
        </div>
      </Tabs>
    </Card>

    <!-- Modal for Add/Edit -->
    <div v-if="showModal" 
         class="fixed inset-0 z-[2000] flex items-center justify-center p-4 bg-zinc-900/60 backdrop-blur-sm animate-in fade-in duration-300">
      <Card class="w-full max-w-md shadow-2xl border border-border animate-in zoom-in-95 bg-card overflow-hidden self-center">
        <CardHeader class="border-b border-border pb-6 relative">
          <div class="flex items-center justify-between">
            <div class="space-y-1">
              <CardTitle class="text-xl font-black tracking-tight uppercase text-foreground">
                {{ editingId ? 'Édition' : 'Création' }}
              </CardTitle>
              <CardDescription class="font-medium italic text-muted-foreground text-xs">Ajustez les détails de la catégorie.</CardDescription>
            </div>
            <Button variant="ghost" size="icon" class="h-8 w-8 text-muted-foreground hover:text-foreground" @click="showModal = false">
              <X class="w-4 h-4" />
            </Button>
          </div>
        </CardHeader>
        
        <CardContent class="py-10">
          <div class="space-y-6">
            <div class="space-y-2">
              <input v-model="form.name" type="text" placeholder="Ex: Immobilier, Dividendes..." 
                     class="w-full bg-muted/30 border border-border rounded-xl p-4 text-lg font-black focus:ring-4 focus:ring-primary/10 outline-none transition-all placeholder:text-muted-foreground/30 text-foreground" 
                     @keyup.enter="saveCategory" />
            </div>

            <div class="space-y-2">
              <label class="text-[10px] font-black uppercase tracking-widest text-muted-foreground ml-1">Catégorie Parente</label>
              <div class="relative">
                <select v-model="form.parentId" 
                        class="w-full bg-muted/30 border border-border rounded-xl p-4 text-sm font-bold focus:ring-4 focus:ring-primary/10 outline-none transition-all appearance-none cursor-pointer text-foreground">
                  <option :value="null" class="bg-card">-- Aucune (Racine) --</option>
                  <option v-for="cat in categoryOptions" :key="cat.id" :value="cat.id" class="bg-card">
                    {{ cat.displayName }}
                  </option>
                </select>
                <div class="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-muted-foreground">
                  <ChevronDown class="w-4 h-4" />
                </div>
              </div>
            </div>
          </div>
        </CardContent>

        <CardFooter class="flex justify-end gap-3 bg-muted/30 p-6 border-t border-border">
          <Button variant="ghost" class="font-bold text-muted-foreground hover:bg-muted" @click="showModal = false">Annuler</Button>
          <Button variant="finance" :disabled="!form.name.trim()" class="min-w-[140px] font-black uppercase tracking-widest h-12 text-xs shadow-xl shadow-black/20" @click="saveCategory">
            <Check class="w-4 h-4 mr-2" />
            Confirmer
          </Button>
        </CardFooter>
      </Card>
    </div>

    <!-- Modal for Merge -->
    <MergeCategoryModal 
      :show="showMergeModal"
      :source-category="sourceCategoryToMerge"
      :all-categories="rawCategories"
      @close="showMergeModal = false"
      @merged="onCategoriesMerged"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import CategoryNode from './Settings/CategoryNode.vue'
import MergeCategoryModal from './Modals/MergeCategoryModal.vue'
import ApiKeyManager from './Settings/ApiKeyManager.vue'
import { 
  Plus, X, Check, FolderTree, RefreshCw, 
  LayoutGrid, ArrowRightLeft, GitMerge, ChevronDown, User as UserIcon, KeyRound,
  FileJson, ExternalLink
} from 'lucide-vue-next'
import { useToast } from '@/composables/useToast'

// UI Components
import { Button } from '@/components/ui/button'
import { Card, CardHeader, CardTitle, CardContent, CardDescription, CardFooter } from '@/components/ui/card'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useGlobalState } from '../../composables/useGlobalState'

const activeTab = ref('account')
const rawCategories = ref([])
const loading = ref(false)
const showModal = ref(false)
const showMergeModal = ref(false)
const editingId = ref(null)
const sourceCategoryToMerge = ref(null)
const form = ref({ name: '', parentId: null })
const { getAuthHeader } = useGlobalState()

const toast = useToast()
const savingAccount = ref(false)
const accountForm = ref({
  email: '',
  firstName: '',
  lastName: ''
})

const fetchAccount = async () => {
  try {
    const res = await fetch('/api/me', {
      headers: getAuthHeader()
    })
    if (res.ok) {
      const data = await res.json()
      accountForm.value = {
        email: data.email,
        firstName: data.firstName || '',
        lastName: data.lastName || ''
      }
    }
  } catch (e) {}
}

const saveAccount = async () => {
  savingAccount.value = true
  try {
    const res = await fetch('/api/me', {
      method: 'POST',
      headers: { 
        ...getAuthHeader(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        firstName: accountForm.value.firstName,
        lastName: accountForm.value.lastName
      })
    })
    if (res.ok) {
      toast.success('Profil mis à jour !')
      window.dispatchEvent(new CustomEvent('finance-user-updated'))
    } else {
      toast.error('Erreur lors de la mise à jour')
    }
  } catch (e) {
    toast.error('Erreur réseau')
  } finally {
    savingAccount.value = false
  }
}

const fetchCategories = async () => {
  loading.value = true
  try {
    const endpoint = activeTab.value === 'investments' ? '/api/investments/categories' : '/api/movements/categories'
    const res = await fetch(endpoint, {
      headers: getAuthHeader()
    })
    if (res.ok) rawCategories.value = await res.json()
  } catch (e) {
    console.error('Failed to fetch categories', e)
  } finally {
    loading.value = false
  }
}

const buildTree = (list, parentId = null) => {
  return list
    .filter(c => c.parentId === parentId)
    .map(c => ({
      ...c,
      children: buildTree(list, c.id)
    }))
}

const treeData = computed(() => buildTree(rawCategories.value))

const getDescendantIds = (list, parentId) => {
  let ids = []
  const children = list.filter(c => c.parentId === parentId)
  for (const child of children) {
    ids.push(child.id)
    ids = ids.concat(getDescendantIds(list, child.id))
  }
  return ids
}

const categoryOptions = computed(() => {
  // Exclude current editing category and its descendants to prevent cycles
  const excludedIds = editingId.value ? [editingId.value, ...getDescendantIds(rawCategories.value, editingId.value)] : []
  const allowed = rawCategories.value.filter(c => !excludedIds.includes(c.id))
  
  const buildFlatTree = (list, parentId = null, depth = 0) => {
    let result = []
    const level = list.filter(c => c.parentId === parentId)
    for (const c of level) {
      result.push({
        ...c,
        displayName: '\u00A0\u00A0\u00A0'.repeat(depth) + (depth > 0 ? '└─ ' : '') + c.name
      })
      result = result.concat(buildFlatTree(list, c.id, depth + 1))
    }
    return result
  }
  
  return buildFlatTree(allowed)
})

watch(activeTab, (newTab) => {
  if (newTab !== 'account' && newTab !== 'api-keys') fetchCategories()
  else if (newTab === 'account') fetchAccount()
})
onMounted(() => {
  if (activeTab.value === 'account') fetchAccount()
  else fetchCategories()
})

const openAddModal = (parentId) => {
  editingId.value = null
  form.value = { name: '', parentId: parentId }
  showModal.value = true
}

const openEditModal = (node) => {
  editingId.value = node.id
  form.value = { 
    name: node.name,
    parentId: node.parentId || null
  }
  showModal.value = true
}

const saveCategory = async () => {
  if (!form.value.name.trim()) return
  
  const endpoint = activeTab.value === 'investments' ? '/api/investments/categories' : '/api/movements/categories'
  const method = editingId.value ? 'PUT' : 'POST'
  const url = editingId.value ? `${endpoint}/${editingId.value}` : endpoint
  
  const payload = {
    name: form.value.name.trim(),
    parentId: form.value.parentId
  }

  try {
    const res = await fetch(url, {
      method,
      headers: { 
        ...getAuthHeader(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    })
    if (res.ok) {
      showModal.value = false
      fetchCategories()
    }
  } catch (e) {
    alert('Erreur lors de l\'enregistrement')
  }
}

const deleteCategory = async (id) => {
  if (!confirm('Êtes-vous sûr de vouloir supprimer cette catégorie ? Les éléments associés seront classés comme "Sans catégorie".')) return
  
  const endpoint = activeTab.value === 'investments' ? '/api/investments/categories' : '/api/movements/categories'
  try {
    const res = await fetch(`${endpoint}/${id}`, {
      method: 'DELETE',
      headers: getAuthHeader()
    })
    if (res.ok) fetchCategories()
  } catch (e) {
    alert('Erreur lors de la suppression')
  }
}

const openMergeModal = (node) => {
  sourceCategoryToMerge.value = node
  showMergeModal.value = true
}

const onCategoriesMerged = () => {
  showMergeModal.value = false
  sourceCategoryToMerge.value = null
  fetchCategories()
}
</script>

<style scoped>
/* No specific scoped styles needed anymore thanks to Tailwind */
</style>
